import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { withRouteRetry } from "@/app/route-retry";
import { loadView, shareViewQuery, viewQuery } from "@/api/views";
import { brandHead, customerHead, withRobots } from "@/lib/head";
import ProjectCameraPage from "@/pages/app/projects/[id]/camera/page";
import ProjectMessagesPage from "@/pages/app/projects/[id]/messages/page";
import ProjectNoticePage from "@/pages/app/projects/[id]/notice/page";
import ProjectTodayPage from "@/pages/app/projects/[id]/today/page";
import CustomerAsPage, { metadata as customerAsMetadata } from "@/pages/c/[token]/as/page";
import LinkErrorPage from "@/pages/c/[token]/error";
import CustomerHousePage, { metadata as customerHouseMetadata } from "@/pages/c/[token]/house/page";
import CustomerProjectLogPage, { metadata as customerProjectLogMetadata } from "@/pages/c/[token]/log/[logId]/page";
import CustomerProjectPage, { metadata as customerProjectMetadata } from "@/pages/c/[token]/project/page";
import ConnectPage, { metadata as connectMetadata } from "@/pages/connect/page";

import { rootRoute } from "./root-route";
import { appRoute, APP_BOUNDARIES, cRoute } from "./w0b";

const RetryingLinkError = withRouteRetry(LinkErrorPage);

interface TodaySearch {
  date?: string;
}

// 주소의 날짜는 사람이 고칠 수 있다 — 문자열만 받고 형식·범위 판정은 core 가 한다.
const validateTodaySearch = (search: Record<string, unknown>): TodaySearch => ({
  date: typeof search.date === "string" ? search.date : undefined,
});

const todayQuery = (id: string, search: TodaySearch) => viewQuery("project-today", { id, ...search });
const messagesQuery = (id: string) => viewQuery("project-messages", { id });
const noticeQuery = (id: string) => viewQuery("project-notice", { id });
const customerProjectQuery = (token: string) => shareViewQuery("c", token, "customer-project");
const customerProjectLogQuery = (token: string, logId: string) => shareViewQuery("c", token, "customer-project-log", { logId });
const customerAsQuery = (token: string) => shareViewQuery("c", token, "customer-as");
const customerHouseQuery = (token: string) => shareViewQuery("c", token, "customer-house");

const ProjectTodayRouteComponent = () => {
  const { id } = projectTodayRoute.useParams();
  const search = projectTodayRoute.useSearch();
  const { data } = useSuspenseQuery(todayQuery(id, search));
  return <ProjectTodayPage data={data} />;
};

const projectTodayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$id/today",
  validateSearch: validateTodaySearch,
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => loadView(context.queryClient, todayQuery(params.id, deps)),
  head: () => brandHead("오늘 기록"),
  component: ProjectTodayRouteComponent,
  ...APP_BOUNDARIES,
});

const ProjectCameraRouteComponent = () => {
  const { id } = projectCameraRoute.useParams();
  const { data } = useSuspenseQuery(todayQuery(id, {}));
  return <ProjectCameraPage data={data} />;
};

// 촬영 — 오늘 기록과 같은 읽기(날짜 없이 = 오늘).
const projectCameraRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$id/camera",
  loader: ({ context, params }) => loadView(context.queryClient, todayQuery(params.id, {})),
  head: () => brandHead("사진 찍기"),
  component: ProjectCameraRouteComponent,
  ...APP_BOUNDARIES,
});

const ProjectMessagesRouteComponent = () => {
  const { id } = projectMessagesRoute.useParams();
  const { data } = useSuspenseQuery(messagesQuery(id));
  return <ProjectMessagesPage data={data} id={id} />;
};

const projectMessagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$id/messages",
  loader: ({ context, params }) => loadView(context.queryClient, messagesQuery(params.id)),
  head: () => brandHead("고객 대화"),
  component: ProjectMessagesRouteComponent,
  ...APP_BOUNDARIES,
});

const ProjectNoticeRouteComponent = () => {
  const { id } = projectNoticeRoute.useParams();
  const { data } = useSuspenseQuery(noticeQuery(id));
  return <ProjectNoticePage data={data} />;
};

const projectNoticeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$id/notice",
  loader: ({ context, params }) => loadView(context.queryClient, noticeQuery(params.id)),
  head: () => brandHead("공사 안내문"),
  component: ProjectNoticeRouteComponent,
  ...APP_BOUNDARIES,
});

const CustomerProjectRouteComponent = () => {
  const { token } = customerProjectRoute.useParams();
  const { data } = useSuspenseQuery(customerProjectQuery(token));
  return <CustomerProjectPage data={data} token={token} />;
};

// 고객 내 현장 — legacy c/[token]/error.tsx 가 덮던 자리 · 오류는 고객용 문구로.
const customerProjectRoute = createRoute({
  getParentRoute: () => cRoute,
  path: "/$token/project",
  loader: ({ context, params }) => loadView(context.queryClient, customerProjectQuery(params.token)),
  head: () => withRobots(customerHead("내 현장"), customerProjectMetadata.robots),
  component: CustomerProjectRouteComponent,
  errorComponent: RetryingLinkError,
});

const CustomerProjectLogRouteComponent = () => {
  const { token, logId } = customerProjectLogRoute.useParams();
  const { data } = useSuspenseQuery(customerProjectLogQuery(token, logId));
  return <CustomerProjectLogPage data={data} token={token} />;
};

// 고객 시공일지 한 건 + 댓글 — 입장·오류는 「내 현장」 과 같다.
const customerProjectLogRoute = createRoute({
  getParentRoute: () => cRoute,
  path: "/$token/log/$logId",
  loader: ({ context, params }) => loadView(context.queryClient, customerProjectLogQuery(params.token, params.logId)),
  head: () => withRobots(customerHead("시공일지"), customerProjectLogMetadata.robots),
  component: CustomerProjectLogRouteComponent,
  errorComponent: RetryingLinkError,
});

const CustomerAsRouteComponent = () => {
  const { token } = customerAsRoute.useParams();
  const { data } = useSuspenseQuery(customerAsQuery(token));
  return <CustomerAsPage data={data} token={token} />;
};

// 고객 A/S 접수 — 입장·오류는 「내 현장」 과 같다.
const customerAsRoute = createRoute({
  getParentRoute: () => cRoute,
  path: "/$token/as",
  loader: ({ context, params }) => loadView(context.queryClient, customerAsQuery(params.token)),
  head: () => withRobots(customerHead("A/S 접수"), customerAsMetadata.robots),
  component: CustomerAsRouteComponent,
  errorComponent: RetryingLinkError,
});

const CustomerHouseRouteComponent = () => {
  const { token } = customerHouseRoute.useParams();
  const { data } = useSuspenseQuery(customerHouseQuery(token));
  return <CustomerHousePage data={data} token={token} />;
};

// 고객 우리 집 기록 — 입장·오류는 「내 현장」 과 같다.
const customerHouseRoute = createRoute({
  getParentRoute: () => cRoute,
  path: "/$token/house",
  loader: ({ context, params }) => loadView(context.queryClient, customerHouseQuery(params.token)),
  head: () => withRobots(customerHead("우리 집 기록"), customerHouseMetadata.robots),
  component: CustomerHouseRouteComponent,
  errorComponent: RetryingLinkError,
});

// 고객 현장 연결 — 로그인 없는 공개 화면(코드 + 전화 끝 4자리) · 검색에는 안 싣는다.
const connectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect",
  head: () => withRobots(brandHead("현장 연결"), connectMetadata.robots),
  component: () => <ConnectPage />,
});

// g3b 배치의 라우트 — getParentRoute 가 가리키는 부모 아래로 root.tsx 가 꽂는다.
export const routes: AnyRoute[] = [
  projectTodayRoute,
  projectCameraRoute,
  projectMessagesRoute,
  projectNoticeRoute,
  customerProjectRoute,
  customerProjectLogRoute,
  customerAsRoute,
  customerHouseRoute,
  connectRoute,
];
